import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useParams } from 'react-router-dom'
import { Minus, Send, RotateCcw, Paperclip } from 'lucide-react'
import { apiPostChat, ChatMessage, ChatUserContext, ChatAttachedImage, ToolCall } from '../../../services/apiService'
import { platformFormatCombos } from './platformDefinitions'
import { useEditor } from '../contexts/EditorContext'
import { useSettings } from '../contexts/SettingsContext'
import { useRegionEdit } from '../contexts/RegionEditContext'
import { useImageRef } from '../contexts/ImageRefContext'
import { useMedia } from '../contexts/MediaContext'
import { useFeedback } from '../contexts/FeedbackContext'
import { composeFeedbackMessage, urlToBase64 } from './chatUtils'
import { PlatformFormatCombo } from './platformDefinitions'
import PromptBuilderCard, { PromptBuilderData } from './PromptBuilderCard'
import ChatMessageList from './ChatMessageList'


const QUICK_ACTION_CHIPS: Record<string, { withImage: string[]; withoutImage: string[] }> = {
  Editing: {
    withImage: ['Craft my prompt', 'Apply brand style', 'What can I do with this image?'],
    withoutImage: ['How do I get started?'],
  },
  Reformat: {
    withImage: ['Reformat for Instagram', 'Reformat for all platforms'],
    withoutImage: ['How does reformat work?'],
  },
  Cropping: {
    withImage: ['Smart crop for multiple formats'],
    withoutImage: ['How does AI Crop work?'],
  },
  Translating: {
    withImage: ['Translate to all languages', 'How does translation work?'],
    withoutImage: ['How does translation work?'],
  },
}

const ALLOWED_TOOL_NAMES = new Set([
  'inpaint',
  'regional_edit',
  'translate',
  'ai_crop',
  'reformat',
])

function fileToBase64(file: File): Promise<ChatAttachedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      const base64 = dataUrl.split(',')[1]
      const media_type = file.type as ChatAttachedImage['media_type']
      resolve({ data: base64, media_type })
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function enforceAspectRatio(
  params: { x_pct: number; y_pct: number; width_pct: number; height_pct: number; aspect_ratio: string },
  naturalW: number,
  naturalH: number,
): { x: number; y: number; width: number; height: number } | null {
  const [aw, ah] = params.aspect_ratio.split(':').map(Number)
  if (!isFinite(aw) || aw <= 0 || !isFinite(ah) || ah <= 0) return null

  const targetAspect = aw / ah

  const x_pct = Math.min(100, Math.max(0, params.x_pct))
  const y_pct = Math.min(100, Math.max(0, params.y_pct))
  const width_pct = Math.min(100, Math.max(0, params.width_pct))
  const height_pct = Math.min(100, Math.max(0, params.height_pct))

  if (!(width_pct > 0) || !(height_pct > 0)) return null

  let w = (width_pct / 100) * naturalW
  let h = (height_pct / 100) * naturalH

  const cx = (x_pct / 100) * naturalW + w / 2
  const cy = (y_pct / 100) * naturalH + h / 2
  const currentAspect = w / h

  if (currentAspect > targetAspect) {
    w = h * targetAspect
  } else {
    h = w / targetAspect
  }

  if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null

  let x = Math.max(0, Math.min(naturalW - w, cx - w / 2))
  let y = Math.max(0, Math.min(naturalH - h, cy - h / 2))
  w = Math.min(w, naturalW - x)
  h = Math.min(h, naturalH - y)

  if (!isFinite(x) || !isFinite(y) || w <= 0 || h <= 0) return null

  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(w),
    height: Math.round(h),
  }
}

const ChatWidget: React.FC<{ hasAccessToAiChat: boolean; isOpen: boolean; onOpen: () => void; onClose: () => void; onCancelAskAI?: (savedPosition?: { historyIndex: number; selectedImageInGroup: number }) => void; onConversationChange?: (hasMessages: boolean) => void }> = ({ hasAccessToAiChat, isOpen, onOpen, onClose, onCancelAskAI, onConversationChange }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])

  useEffect(() => {
    onConversationChange?.(messages.length > 0);
  }, [messages.length, onConversationChange]);

  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [pendingImages, setPendingImages] = useState<ChatAttachedImage[]>([])
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([])
  const [compareWithSource, setCompareWithSource] = useState(false)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [executedActions, setExecutedActions] = useState<Set<number>>(new Set())
  const [showPromptBuilder, setShowPromptBuilder] = useState(false)
  const handleGenerateRef = useRef<() => void>(() => {})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const sendMessageRef = useRef<(text: string, images?: ChatAttachedImage[]) => void>(() => {})

  const { project_id: projectId } = useParams<{ project_id: string }>()
  const { prompts, setPrompts, editModel, setEditModel, activeTab, setActiveTab, currentImage, imageGroups, isLoading: editorIsLoading, handleGenerate, handleAutoTranslate, handleApplyAICrop, handleReformat } = useEditor()
  handleGenerateRef.current = handleGenerate
  const { useBrandGuidelines, setUseBrandGuidelines, setExcludedAssetIds, brandAssets } = useSettings()
  const { isRegionSelectMode, setIsRegionSelectMode, setRegionAspect, setDirectEditRegion } = useRegionEdit()
  const { imgSize } = useImageRef()
  const { secondaryImages } = useMedia()
  const { pendingFeedbackForChat, setPendingFeedbackForChat, setIsFeedbackPanelExpanded } = useFeedback()

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  // Find the source image (the image used to generate the current one)
  const previousImageUrl = useMemo(() => {
    const sourcePictureId = currentImage?.meta?.source_picture_id
    if (!sourcePictureId || !imageGroups.length) return null
    for (const group of imageGroups) {
      for (const img of group.images) {
        if (img.picture_id === sourcePictureId) return img.output_url
      }
    }
    return null
  }, [currentImage, imageGroups])

  const getUserContext = useCallback((): ChatUserContext => ({
    prompt: prompts.filter(p => p.trim()).join('\n') || null,
    model: editModel || null,
    brand_guidelines_enabled: useBrandGuidelines,
    regional_editing_enabled: isRegionSelectMode,
    reference_photos_count: secondaryImages?.length || 0,
    image_url: currentImage?.output_url || null,
    previous_image_url: previousImageUrl,
    include_previous_image: compareWithSource,
  }), [prompts, editModel, useBrandGuidelines, isRegionSelectMode, secondaryImages, currentImage, previousImageUrl, compareWithSource])

  const sendMessage = useCallback(async (text: string, images?: ChatAttachedImage[]) => {
    if (!text.trim() || isStreaming) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: text.trim(),
      ...(images && images.length > 0 ? { images } : {}),
    }
    setMessages(prev => [...prev, userMessage])
    setInput('')
    setPendingImages([])
    pendingPreviews.forEach(url => URL.revokeObjectURL(url))
    setPendingPreviews([])
    setIsStreaming(true)

    setMessages(prev => [...prev, { role: 'assistant', content: '' }])

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const response = await apiPostChat(
        text.trim(),
        activeTab,
        getUserContext(),
        messages.concat(userMessage),
        abortController.signal,
        images,
        projectId
      )

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      let streamDone = false
      while (!streamDone) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6)

          if (data === '[DONE]') { streamDone = true; break }

          try {
            const parsed = JSON.parse(data)
            if (parsed.error) {
              setMessages(prev => {
                if (prev.length === 0) return prev
                const updated = [...prev]
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: parsed.error,
                }
                return updated
              })
              break
            }
            if (parsed.type === 'text' && parsed.text !== undefined) {
              setMessages(prev => {
                if (prev.length === 0) return prev
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: last.content + parsed.text,
                }
                return updated
              })
            } else if (parsed.type === 'tool_use') {
              if (!ALLOWED_TOOL_NAMES.has(parsed.name)) {
                // Backend proposed a tool that is no longer exposed in the UI.
                // Drop the card so no Proceed button renders; any preceding
                // assistant text remains visible.
                continue
              }
              // Show the planned tool call in the chat card only.
              // Actual state mutations (prompt, excluded_asset_ids,
              // use_brand_guidelines, model, etc.) are applied in executeAction
              // when the user clicks Proceed — so the UI stays in sync with
              // what's about to run, not with what was just proposed.
              setMessages(prev => {
                if (prev.length === 0) return prev
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = {
                  ...last,
                  toolCall: { name: parsed.name, params: parsed.params },
                }
                return updated
              })
            }
            // Backward compat: handle old format without type field
            else if (parsed.text !== undefined && !parsed.type) {
              setMessages(prev => {
                if (prev.length === 0) return prev
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: last.content + parsed.text,
                }
                return updated
              })
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setMessages(prev => {
          const updated = [...prev]
          if (updated[updated.length - 1]?.role === 'assistant' && !updated[updated.length - 1].content) {
            updated[updated.length - 1] = {
              role: 'assistant',
              content: 'Sorry, something went wrong. Please try again.',
            }
          }
          return updated
        })
      }
    } finally {
      setIsStreaming(false)
      abortControllerRef.current = null
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [activeTab, getUserContext, isStreaming, messages, pendingPreviews, projectId])

  // Keep ref in sync so processFeedbackForChat always calls the latest version
  sendMessageRef.current = sendMessage

  const processFeedbackForChat = useCallback(async (feedback: NonNullable<typeof pendingFeedbackForChat>) => {
    setIsFeedbackPanelExpanded(false)
    onOpen()
    setMessages([])
    setInput('')
    setPendingImages([])
    pendingPreviews.forEach(url => URL.revokeObjectURL(url))
    setPendingPreviews([])
    setCompareWithSource(false)
    setShowClearConfirm(false)
    setShowPromptBuilder(false)
    setExecutedActions(new Set())

    const text = composeFeedbackMessage(
      feedback.comment,
      feedback.replies,
      feedback.coordinates,
    )

    // Only attach the feedback image if it's NOT the current editor image
    // (the current image is already sent via image_url in the user context)
    let images: ChatAttachedImage[] | undefined
    if (feedback.imageUrl && feedback.imageUrl !== currentImage?.output_url) {
      const img = await urlToBase64(feedback.imageUrl)
      if (img) images = [img]
    }

    setPendingFeedbackForChat(null)

    // Use setTimeout to ensure state updates from the reset above have flushed.
    // Call via ref so we get the latest sendMessage (with empty messages array).
    setTimeout(() => {
      sendMessageRef.current(text, images)
    }, 0)
  }, [currentImage, pendingPreviews, setPendingFeedbackForChat, setIsFeedbackPanelExpanded, onOpen])

  useEffect(() => {
    if (!pendingFeedbackForChat) return
    if (messages.length > 0) {
      setIsFeedbackPanelExpanded(false)
      onOpen()
      setShowClearConfirm(true)
    } else {
      processFeedbackForChat(pendingFeedbackForChat)
    }
  // Intentionally omits messages and processFeedbackForChat — only triggers on new feedback arrival
  }, [pendingFeedbackForChat]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePromptBuilderSubmit = useCallback((data: PromptBuilderData) => {
    const parts: string[] = []
    parts.push(`**Edit:** ${data.editIntent}`)
    if (data.preserve.size > 0) parts.push(`**Preserve:** ${[...data.preserve].join(', ')}`)
    if (data.toneAudience.trim()) parts.push(`**Tone & audience:** ${data.toneAudience.trim()}`)
    if (data.placement) parts.push(`**Placement:** ${data.placement}`)
    if (data.mustHaveText.trim()) parts.push(`**Must-have text:** "${data.mustHaveText.trim()}"`)
    setShowPromptBuilder(false)
    sendMessage(
      `Craft a precise editing prompt from these requirements:\n\n${parts.join('\n')}\n\nUse this to call the appropriate tool (inpaint or regional_edit). Write the prompt yourself — do not ask me to refine it.`
    )
  }, [sendMessage])

  const handleChipClick = useCallback((chip: string) => {
    if (chip === 'Craft my prompt') {
      setShowPromptBuilder(true)
    } else {
      sendMessage(chip)
    }
  }, [sendMessage])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input, pendingImages.length > 0 ? pendingImages : undefined)
    }
  }

  const handleFileSelect = async (files: FileList | null) => {
    if (!files) return
    const newImages: ChatAttachedImage[] = []
    const newPreviews: string[] = []
    for (let i = 0; i < Math.min(files.length, 5 - pendingImages.length); i++) {
      const file = files[i]
      if (!file.type.startsWith('image/')) continue
      const img = await fileToBase64(file)
      newImages.push(img)
      newPreviews.push(URL.createObjectURL(file))
    }
    setPendingImages(prev => [...prev, ...newImages])
    setPendingPreviews(prev => [...prev, ...newPreviews])
  }

  const removePendingImage = (index: number) => {
    setPendingPreviews(prev => {
      URL.revokeObjectURL(prev[index])
      return prev.filter((_, i) => i !== index)
    })
    setPendingImages(prev => prev.filter((_, i) => i !== index))
  }

  const executeAction = useCallback((toolCall: ToolCall, messageIndex: number) => {
    if (!currentImage) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Please select an image first.' }])
      return
    }

    setExecutedActions(prev => new Set(prev).add(messageIndex))

    const { name: op, params } = toolCall

    switch (op) {
      case 'inpaint':
        if (params.prompt) setPrompts([params.prompt])
        if (params.model) setEditModel(params.model)
        if (params.use_brand_guidelines !== undefined) setUseBrandGuidelines(params.use_brand_guidelines)
        if (params.excluded_asset_ids) setExcludedAssetIds(params.excluded_asset_ids)
        // setTimeout lets React re-render with new prompts/model, then ref has the updated handleGenerate
        setTimeout(() => handleGenerateRef.current(), 0)
        break
      case 'translate':
        if (params.input_language && params.output_languages) {
          handleAutoTranslate(params.input_language, params.output_languages)
        }
        break
      case 'ai_crop':
        if (params.formats) {
          handleApplyAICrop(params.formats)
        }
        break
      case 'reformat': {
        if (!params.combos) break
        const resolved = params.combos
          .map((c: { platform_label: string; label: string }) =>
            platformFormatCombos.find(
              (p) => p.platformLabel === c.platform_label && p.label === c.label
            )
          )
          .filter((combo: PlatformFormatCombo | undefined): combo is PlatformFormatCombo => Boolean(combo))
        if (resolved.length === 0) {
          setMessages(prev => [...prev, { role: 'assistant' as const, content: 'I could not match any requested reformat targets.' }])
          break
        }
        handleReformat({ combos: resolved })
        break
      }
      case 'regional_edit': {
        const hasAutoRegion = params.aspect_ratio &&
          params.x_pct != null && params.y_pct != null &&
          params.width_pct != null && params.height_pct != null

        setActiveTab('Editing')
        if (params.prompt) setPrompts([params.prompt])
        // Model is forced to 'google' inside apiPostRegionalEdit; ignore params.model.
        if (params.use_brand_guidelines !== undefined) setUseBrandGuidelines(params.use_brand_guidelines)
        if (params.excluded_asset_ids) setExcludedAssetIds(params.excluded_asset_ids)

        if (hasAutoRegion && imgSize) {
          const region = enforceAspectRatio(
            {
              x_pct: params.x_pct,
              y_pct: params.y_pct,
              width_pct: params.width_pct,
              height_pct: params.height_pct,
              aspect_ratio: params.aspect_ratio,
            },
            imgSize.width,
            imgSize.height,
          )
          if (region) {
            const [aw, ah] = params.aspect_ratio.split(':').map(Number)
            setRegionAspect(aw / ah)
            setDirectEditRegion(region)
            setIsRegionSelectMode(true)
            setTimeout(() => handleGenerateRef.current(), 0)
          } else {
            setDirectEditRegion(null)
            setIsRegionSelectMode(true)
          }
        } else {
          setDirectEditRegion(null)
          setIsRegionSelectMode(true)
        }
        break
      }
    }
  }, [currentImage, setPrompts, setEditModel, setUseBrandGuidelines, setExcludedAssetIds, handleAutoTranslate, handleApplyAICrop, handleReformat, setActiveTab, setIsRegionSelectMode, setRegionAspect, setDirectEditRegion, imgSize])

  const chips = useMemo(() => {
    const tabChips = QUICK_ACTION_CHIPS[activeTab] || QUICK_ACTION_CHIPS['Editing']
    return currentImage ? tabChips.withImage : tabChips.withoutImage
  }, [activeTab, currentImage])

  if (!hasAccessToAiChat) return null
  if (!isOpen) return null

  return (() => {
        const inner = (
          <>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white">
            <span className="font-semibold text-sm">AI Conic Assistant</span>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={() => { abortControllerRef.current?.abort(); abortControllerRef.current = null; setIsStreaming(false); setMessages([]); setInput(''); setPendingImages([]); pendingPreviews.forEach(url => URL.revokeObjectURL(url)); setPendingPreviews([]); setCompareWithSource(false); setExecutedActions(new Set()); setShowPromptBuilder(false) }}
                  className="hover:bg-white/20 rounded p-1 transition-colors"
                  aria-label="Reset conversation"
                  title="New conversation"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="hover:bg-white/20 rounded p-1 transition-colors"
                aria-label="Minimize assistant"
              >
                <Minus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showClearConfirm && pendingFeedbackForChat && (
            <div className="absolute inset-0 z-10 bg-black/50 flex items-center justify-center rounded-xl">
              <div className="bg-white rounded-lg p-4 mx-4 shadow-xl max-w-[280px]">
                <p className="text-sm text-gray-900 mb-3">
                  This will clear your current conversation. Continue?
                </p>
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      const saved = pendingFeedbackForChat?.savedPosition
                      setShowClearConfirm(false)
                      setPendingFeedbackForChat(null)
                      onCancelAskAI?.(saved)
                    }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => processFeedbackForChat(pendingFeedbackForChat)}
                    className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}
          <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {messages.length === 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-900">
                      Hi! I can help you edit images, apply your brand style, or improve your prompts. What would you like to do?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {chips.map((chip, i) => (
                        <button
                          key={i}
                          onClick={() => handleChipClick(chip)}
                          className="text-sm px-3 py-1.5 rounded-full bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                    {showPromptBuilder && (
                      <PromptBuilderCard
                        onSubmit={handlePromptBuilderSubmit}
                        onCancel={() => setShowPromptBuilder(false)}
                      />
                    )}
                  </div>
                ) : (
                  <ChatMessageList
                    messages={messages}
                    isStreaming={isStreaming}
                    executedActions={executedActions}
                    editorIsLoading={editorIsLoading}
                    brandAssets={brandAssets}
                    onExecuteAction={executeAction}
                  />
                )}
                {showPromptBuilder && messages.length > 0 && (
                  <PromptBuilderCard
                    onSubmit={handlePromptBuilderSubmit}
                    onCancel={() => setShowPromptBuilder(false)}
                  />
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Pending image previews */}
              {pendingPreviews.length > 0 && (
                <div className="px-4 py-2 border-t border-gray-100 flex gap-2 overflow-x-auto">
                  {pendingPreviews.map((src, i) => (
                    <div key={i} className="relative shrink-0">
                      <img src={src} alt="" className="w-12 h-12 rounded object-cover border border-gray-200" />
                      <button
                        onClick={() => removePendingImage(i)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] leading-none"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Compare toggle + Input */}
              <div className="px-4 py-3 border-t border-gray-200">
                {previousImageUrl && (
                  <button
                    onClick={() => setCompareWithSource(prev => !prev)}
                    className={`flex items-center gap-1.5 text-xs mb-2 px-2.5 py-1 rounded-md border transition-all duration-200 ${
                      compareWithSource
                        ? 'bg-purple-50 text-purple-700 border-purple-300 shadow-sm'
                        : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-500'
                    }`}
                  >
                    <div className={`w-6 h-3.5 rounded-full flex items-center transition-all duration-200 ${
                      compareWithSource ? 'bg-purple-500 justify-end' : 'bg-gray-300 justify-start'
                    }`}>
                      <div className="w-2.5 h-2.5 rounded-full bg-white mx-0.5 shadow-sm" />
                    </div>
                    Compare with source
                  </button>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={e => { handleFileSelect(e.target.files); e.target.value = '' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isStreaming || pendingImages.length >= 5}
                    className="p-2 rounded-lg text-gray-500 hover:text-purple-600 hover:bg-purple-50 disabled:opacity-40 transition-colors"
                    aria-label="Attach image"
                    title="Attach image"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything..."
                    rows={1}
                    className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-black focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={isStreaming}
                  />
                  <button
                    onClick={() => sendMessage(input, pendingImages.length > 0 ? pendingImages : undefined)}
                    disabled={(!input.trim() && pendingImages.length === 0) || isStreaming}
                    className="p-2 rounded-lg bg-purple-600 text-white disabled:opacity-40 hover:bg-purple-700 transition-colors"
                    aria-label="Send message"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
          </>
          </>
        )
        const target = document.getElementById('chat-panel-portal')
        if (!target) return null
        return createPortal(
          <div data-testid="chat-panel-expanded" className="relative flex-1 bg-white border border-gray-700 rounded-lg shadow-2xl flex flex-col overflow-hidden">
            {inner}
          </div>,
          target
        )
  })()
}

export default ChatWidget
